'use client';

import React, { useState } from 'react';
import { X, Save, Loader2, UserCheck } from 'lucide-react';
import { entriesService } from '@/lib/services/entriesService';

interface SalesRepModalProps {
  entryId: string;
  onClose: () => void;
  onSaved?: () => void;
}

const PRODUCTS = [
  { id: 'IDE', label: 'IDE' },
  { id: '3TL', label: '3TL' },
  { id: 'Access Manager', label: 'Access Manager' },
];

const REPS = ['Dominique', 'Saket', 'Chandan', 'Other'];

export default function SalesRepModal({ entryId, onClose, onSaved }: SalesRepModalProps) {
  const [notes, setNotes] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [repName, setRepName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const toggleProduct = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((p) => p !== productId)
        : [...prev, productId]
    );
  };

  const handleSave = async () => {
    if (!repName) {
      setError('Please select your name');
      return;
    }
    setError('');
    setIsSaving(true);
    try {
      await entriesService.saveSalesRepData(entryId, {
        repName,
        productInterest: selectedProducts,
        notes,
      });
      setSaved(true);
      onSaved?.();
      setTimeout(() => onClose(), 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center">
              <UserCheck size={18} className="text-indigo-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-base">Sales Rep Notes</h3>
              <p className="text-xs text-gray-400">Add details for this player</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
          >
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Rep Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Your Name <span className="text-red-400">*</span>
            </label>
            <select
              value={repName}
              onChange={(e) => { setRepName(e.target.value); setError(''); }}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
            >
              <option value="">Select your name...</option>
              {REPS.map((rep) => (
                <option key={rep} value={rep}>{rep}</option>
              ))}
            </select>
          </div>

          {/* Product Interest */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Product Interest <span className="text-gray-400 font-normal">(select all that apply)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {PRODUCTS.map((product) => {
                const isSelected = selectedProducts.includes(product.id);
                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => toggleProduct(product.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all duration-150 ${
                      isSelected
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                    }`}
                  >
                    {isSelected && <span className="mr-1">✓</span>}
                    {product.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notes <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this player..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 placeholder:text-gray-400"
            />
          </div>

          {error && (
            <p className="text-sm text-red-500 font-medium">{error}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-2xl border-2 border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || saved}
              className={`flex-1 py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all duration-150 ${
                saved
                  ? 'bg-green-500 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md disabled:opacity-60'
              }`}
            >
              {saved ? (
                <>✓ Saved!</>
              ) : isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Notes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
